import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";

const AuthContext = createContext(null);
export const useAuth = () => useContext(AuthContext);

const EMERGENT_AUTH = "https://auth.emergentagent.com/";

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = loading, false = anon
  const [notifications, setNotifications] = useState([]);

  const loadNotifications = useCallback(async () => {
    try {
      const { data } = await api.get("/notifications");
      setNotifications(data);
    } catch {
      setNotifications([]);
    }
  }, []);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      loadNotifications();
    } catch {
      setUser(false);
    }
  }, [loadNotifications]);

  useEffect(() => {
    // Handle Emergent Google OAuth redirect (session_id in URL hash)
    const hash = window.location.hash;
    const match = hash.match(/session_id=([^&]+)/);
    if (match) {
      const sessionId = decodeURIComponent(match[1]);
      window.history.replaceState(null, "", window.location.pathname);
      api
        .post("/auth/google", { session_id: sessionId })
        .then(({ data }) => {
          setUser(data);
          loadNotifications();
        })
        .catch(() => refresh());
      return;
    }
    refresh();
  }, [refresh, loadNotifications]);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    setUser(data);
    loadNotifications();
    return data;
  };

  const register = async (name, email, password) => {
    const { data } = await api.post("/auth/register", { name, email, password });
    setUser(data);
    loadNotifications();
    return data;
  };

  const loginWithGoogle = () => {
    const redirect = window.location.origin + "/dashboard";
    window.location.href = `${EMERGENT_AUTH}?redirect=${encodeURIComponent(redirect)}`;
  };

  const logout = async () => {
    await api.post("/auth/logout");
    setUser(false);
    setNotifications([]);
  };

  const markRead = async (id) => {
    await api.post(`/notifications/${id}/read`);
    loadNotifications();
  };

  return (
    <AuthContext.Provider
      value={{ user, notifications, login, register, loginWithGoogle, logout, refresh, loadNotifications, markRead }}
    >
      {children}
    </AuthContext.Provider>
  );
}
